import * as yup from "yup";
import { setLocale } from "yup";

// Set custom validation messages
setLocale({
	mixed: {
		required: "Required",
	},
	string: {
		// eslint-disable-next-line
		max: "Must be ${max} characters or less",
		// eslint-disable-next-line
		min: "Must be ${min} characters or more",
		// eslint-disable-next-line
		length: "Must be ${length} characters long",
		matches: "Invalid format",
	},
	number: {
		// eslint-disable-next-line
		max: "Must be ${max} or less",
		// eslint-disable-next-line
		min: "Must be ${min} or more",
		positive: "Must be a positive number",
		integer: "Must be a whole number",
	},
});

/**
 * Custom transform function for number fields that handles both:
 * - Formatted strings from react-number-format (e.g., "$1,234.56", "1,234")
 * - Regular number inputs
 * - Empty strings
 */
const transformToNumber = (value, originalValue) => {
	// If originalValue is already a number, return it
	if (typeof originalValue === "number") {
		return originalValue;
	}

	// If it's an empty string or null/undefined, return null
	if (
		originalValue === "" ||
		originalValue === null ||
		originalValue === undefined
	) {
		return null;
	}

	// If it's a string, remove formatting characters and parse
	if (typeof originalValue === "string") {
		// Remove currency symbols, commas, spaces, and other formatting
		const cleaned = originalValue.replace(/[$,\s]/g, "");

		// If the cleaned string is empty, return null
		if (cleaned === "") {
			return null;
		}

		// Parse the cleaned string
		const parsed = parseFloat(cleaned);

		// Return the parsed number if valid, otherwise null
		return isNaN(parsed) ? null : parsed;
	}

	// Default fallback
	return null;
};

/**
 * Inventory validation schema based on database constraints
 * Maps to the inventory table schema with proper field name casing
 */
export const inventoryValidationSchema = yup.object().shape({
	// Required core fields
	stockNo: yup
		.string()
		.required()
		.min(1)
		.max(20, "Stock number must be 20 characters or less"),

	vin: yup
		.string()
		.required()
		.max(20, "VIN must be 20 characters or less")
		.matches(/^[A-HJ-NPR-Z0-9-]*$/, "VIN contains invalid characters"),

	year: yup
		.date()
		.max(new Date().getFullYear() + 1)
		.min(new Date("January 01, 1900 23:15:00").getFullYear())
		.nullable()
		.transform((curr, orig) => (orig?.length === 4 ? curr : null))
		.required("Required (YYYY)"),

	make: yup
		.string()
		.required()
		.max(100, "Make must be 100 characters or less"),

	model: yup
		.string()
		.required()
		.max(100, "Model must be 100 characters or less"),

	dateIn: yup
		.string()
		.required()
		.length(10, "Required format is YYYY-MM-DD"),

	// Optional string fields with max lengths
	trim: yup.string().max(50).nullable(),
	subModel: yup.string().max(100).nullable(),
	color: yup.string().max(100).nullable(),
	intColor: yup.string().max(100).nullable(),
	engine: yup.string().max(100).nullable(),
	transmission: yup.string().max(100).nullable(),
	driveTrain: yup.string().max(50).nullable(),
	fuelType: yup.string().max(10).nullable(),
	condition: yup.string().max(30).nullable(),
	status: yup.string().max(20).nullable(),
	type: yup.string().max(20).nullable(),
	newUsed: yup.string().max(4).nullable(),

	// VIN2 for special vehicles
	vin2: yup
		.string()
		.max(20, "VIN2 must be 20 characters or less")
		.nullable(),

	// Odometer fields
	odometerIn: yup
		.number()
		.integer()
		.min(0, "Odometer must be 0 or greater")
		.max(999999999, "Odometer value too large")
		.nullable()
		.transform(transformToNumber),

	odometerOut: yup
		.number()
		.integer()
		.min(0, "Odometer must be 0 or greater")
		.max(999999999, "Odometer value too large")
		.nullable()
		.transform(transformToNumber),

	// License and registration
	license: yup.string().max(10).nullable(),
	titleNo: yup.string().max(20).nullable(),
	titleState: yup.string().max(5).nullable(),
	newTitleState: yup.string().max(5).nullable(),
	newTitleNumber: yup.string().max(50).nullable(),
	titleBrand: yup.string().max(50).nullable(),
	reportOfSale: yup.string().max(15).nullable(),

	// Money fields (all nullable)
	askingPrice: yup
		.number()
		.min(0, "Price cannot be negative")
		.nullable()
		.transform(transformToNumber),

	cost: yup
		.number()
		.min(0, "Cost cannot be negative")
		.nullable()
		.transform(transformToNumber),

	msrp: yup
		.number()
		.min(0, "MSRP cannot be negative")
		.nullable()
		.transform(transformToNumber),

	pack: yup.number().nullable().transform(transformToNumber),

	holdBack: yup.number().nullable().transform(transformToNumber),

	incentive: yup.number().nullable().transform(transformToNumber),

	totalRecon: yup
		.number()
		.min(0, "Total recon cannot be negative")
		.nullable()
		.transform(transformToNumber),

	totalOtherCost: yup
		.number()
		.min(0, "Total other cost cannot be negative")
		.nullable()
		.transform(transformToNumber),

	totalCost: yup
		.number()
		.min(0, "Total cost cannot be negative")
		.nullable()
		.transform(transformToNumber),

	downPmt: yup.number().nullable().transform(transformToNumber),

	weightFee: yup
		.number()
		.min(0, "Weight fee cannot be negative")
		.nullable()
		.transform(transformToNumber),

	acv: yup.number().nullable().transform(transformToNumber),

	allowance: yup.number().nullable().transform(transformToNumber),

	payoff: yup.number().nullable().transform(transformToNumber),

	wholesalePrice: yup
		.number()
		.min(0, "Wholesale price cannot be negative")
		.nullable()
		.transform(transformToNumber),

	onlinePrice: yup.string().max(20).nullable(),
	onlineBuyNowPrice: yup.string().max(20).nullable(),
	cargurusPrice: yup.number().nullable().transform(transformToNumber),

	// Vehicle specifications
	weight: yup
		.number()
		.integer()
		.min(0, "Weight cannot be negative")
		.nullable()
		.transform(transformToNumber),

	doors: yup
		.number()
		.integer()
		.min(0)
		.max(32767, "Doors value too large")
		.nullable()
		.transform(transformToNumber),

	numCylinders: yup.string().max(2).nullable(),

	mPGHwy: yup.string().max(9).nullable(),
	mPGCity: yup.string().max(39).nullable(),

	horsePower: yup.string().max(10).nullable(),

	// Location and buyer
	location: yup.string().max(20).nullable(),
	buyer: yup.string().max(20).nullable(),
	vendorName: yup.string().max(100).nullable(),

	// Payment information
	paymentMethod: yup.string().max(100).nullable(),
	checkNo: yup.string().max(100).nullable(),

	// Descriptions (max length fields)
	tag: yup.string().max(100).nullable(),
	description: yup.string().nullable(),
	longDescription: yup.string().nullable(),

	// Equipment fields (max length)
	customEquip: yup.string().nullable(),
	selectedEquip: yup.string().nullable(),
	optionalEquip: yup.string().nullable(),
	standardEquip: yup.string().nullable(),
	specificationEquip: yup.string().nullable(),

	// Flooring information
	flooringComp: yup.string().max(50).nullable(),
	flooringInt: yup
		.number()
		.min(0, "Flooring interest cannot be negative")
		.nullable()
		.transform(transformToNumber),

	flooringAmt: yup
		.number()
		.min(0, "Flooring amount cannot be negative")
		.nullable()
		.transform(transformToNumber),

	flooringValue: yup.number().nullable().transform(transformToNumber),

	flooringAdminFee: yup
		.number()
		.min(0, "Admin fee cannot be negative")
		.nullable()
		.transform(transformToNumber),

	flooringCheckNo: yup.string().max(9).nullable(),
	flooring: yup.number().nullable().transform(transformToNumber),

	// GPS information
	gps_ID: yup.string().max(20).nullable(),
	gpsCompany: yup.string().max(50).nullable(),
	gpsWebsite: yup.string().max(150).nullable(),
	gps_ID2: yup.string().max(20).nullable(),
	gpsCompany2: yup.string().max(50).nullable(),
	gpsWebsite2: yup.string().max(150).nullable(),

	// RV specific fields
	rvType: yup.string().max(50).nullable(),
	rvChassis: yup.string().max(50).nullable(),
	rvLength: yup.string().max(50).nullable(),
	rvEngine: yup.string().max(50).nullable(),
	rvSlideOuts: yup.string().max(9).nullable(),
	rvFuelType: yup.string().max(9).nullable(),
	rvSleepCount: yup.string().max(9).nullable(),
	rvGvrWeight: yup.string().max(9).nullable(),
	rvDryWeight: yup.string().max(9).nullable(),
	rvCaption: yup.string().max(255).nullable(),

	// Trailer information
	trailerMake: yup.string().max(40).nullable(),
	trailerModel: yup.string().max(40).nullable(),
	trailerYear: yup.string().max(4).nullable(),
	trailerVin: yup.string().max(20).nullable(),
	trailerLicense: yup.string().max(50).nullable(),
	trailerAxle: yup.string().max(2).nullable(),
	trailerStatus: yup.string().max(50).nullable(),
	trailerPrice: yup.number().nullable().transform(transformToNumber),

	// Truck specific fields
	height: yup.string().max(15).nullable(),
	length: yup.string().max(15).nullable(),
	tireSize: yup.string().max(15).nullable(),
	tireType: yup.string().max(15).nullable(),
	tireStatus: yup.string().max(15).nullable(),
	tireFee: yup.number().nullable().transform(transformToNumber),

	pullWeight: yup.string().max(50).nullable(),
	engine2: yup.string().max(100).nullable(),
	engine2Make: yup.string().max(50).nullable(),
	engine2Year: yup.string().max(4).nullable(),
	engine2Status: yup.string().max(50).nullable(),
	engine2Price: yup.number().nullable().transform(transformToNumber),

	engineBrake: yup
		.number()
		.integer()
		.min(0)
		.max(32767)
		.nullable()
		.transform(transformToNumber),

	diffRatio: yup.string().max(15).nullable(),
	tankCount: yup
		.number()
		.integer()
		.min(0)
		.max(32767)
		.nullable()
		.transform(transformToNumber),

	tankCapacity: yup
		.number()
		.min(0)
		.nullable()
		.transform(transformToNumber),

	axelFrontMake: yup.string().max(50).nullable(),
	axelRearMake: yup.string().max(50).nullable(),
	axelCount: yup
		.number()
		.integer()
		.min(0)
		.max(32767)
		.nullable()
		.transform(transformToNumber),

	axelType: yup.string().max(50).nullable(),
	hiRoof: yup.string().max(50).nullable(),
	sleeper: yup
		.number()
		.integer()
		.min(0)
		.max(32767)
		.nullable()
		.transform(transformToNumber),

	sleeperSize: yup.string().max(50).nullable(),
	sleeperBeds: yup.string().max(50).nullable(),
	cabStyle: yup.string().max(50).nullable(),

	// Permit information
	permitNumber: yup.string().max(50).nullable(),
	tempPlate: yup.string().max(9).nullable(),

	// Color hex codes
	colorHex: yup
		.string()
		.max(9)
		.matches(/^#?([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})?$/, "Invalid hex color code")
		.nullable(),

	intColorHex: yup
		.string()
		.max(9)
		.matches(/^#?([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})?$/, "Invalid hex color code")
		.nullable(),

	// Shipping information
	shipTransportCompany: yup.string().max(50).nullable(),
	shipStateOfTitle: yup.string().max(50).nullable(),

	// Video URL
	videoURL: yup
		.string()
		.url("Must be a valid URL")
		.nullable()
		.transform((value) => (value === "" ? null : value)),

	// Other fields
	reminders: yup.string().nullable(),
	notes: yup.string().nullable(),

	// Integer fields for hours and equipment specifications
	hours: yup
		.number()
		.integer()
		.min(0)
		.nullable()
		.transform(transformToNumber),

	engineManufacturer: yup.string().max(100).nullable(),
	engineSerialNumber: yup.string().max(100).nullable(),
	engineDisplacement: yup.string().max(100).nullable(),
	transmissionManufacturer: yup.string().max(100).nullable(),
	transmissionType: yup.string().max(100).nullable(),
	numberOfSpeeds: yup
		.number()
		.integer()
		.min(0)
		.nullable()
		.transform(transformToNumber),

	ratio: yup.string().max(30).nullable(),
	drive: yup.string().max(30).nullable(),
	suspension: yup.string().max(100).nullable(),

	frontAxleWeight: yup
		.number()
		.integer()
		.min(0)
		.nullable()
		.transform(transformToNumber),

	rearAxleWeight: yup
		.number()
		.integer()
		.min(0)
		.nullable()
		.transform(transformToNumber),

	wheels: yup.string().max(100).nullable(),
	wheelbase: yup.string().max(100).nullable(),
	fifthWheelType: yup.string().max(100).nullable(),
	brakeSystemType: yup.string().max(100).nullable(),

	wholesaleBook: yup.number().nullable().transform(transformToNumber),

	warranty: yup
		.number()
		.integer()
		.min(0)
		.max(255)
		.nullable()
		.transform(transformToNumber),

	// Category fields
	category: yup
		.number()
		.integer()
		.nullable()
		.transform(transformToNumber),

	subCategory: yup
		.number()
		.integer()
		.nullable()
		.transform(transformToNumber),

	// Date validation helpers removed from here since dates are typically strings in YYYY-MM-DD format
	// and are handled by the backend
});

/**
 * Minimal validation schema for required fields only
 * Use this for forms where only core fields need validation
 */
export const inventoryMinimalSchema = yup.object().shape({
	stockNo: yup
		.string()
		.required()
		.min(1)
		.max(20, "Stock number must be 20 characters or less"),

	vin: yup
		.string()
		.required()
		.max(20, "VIN must be 20 characters or less"),

	year: yup
		.date()
		.max(new Date().getFullYear() + 1)
		.min(new Date("January 01, 1900 23:15:00").getFullYear())
		.nullable()
		.transform((curr, orig) => (orig?.length === 4 ? curr : null))
		.required("Required (YYYY)"),

	make: yup.string().required().max(100),
	model: yup.string().required().max(100),
	dateIn: yup.string().required().length(10, "Required format is YYYY-MM-DD"),
});

export default inventoryValidationSchema;
